// Paris-intent SEO landing pages (1/2) — each answers a DIFFERENT search intent.
const page = (en, fr) => ({ en, fr });

export const LANDINGS_PARIS_1 = {
  "private-assistant-paris": page(
    {
      seoTitle: "Personal Assistant in Paris — English-Speaking, Discreet | Book & Smile",
      seoDesc: "Your English-speaking personal assistant in Paris: reservations, appointments, French administration, errands, sourcing and special requests. Discreet, on the ground.",
      eyebrow: "Personal Assistant · Paris",
      h1a: "Personal Assistant",
      h1b: "in Paris.",
      answer: "A discreet pair of hands in Paris. Book & Smile gives you an English-speaking private personal assistant on the ground — for reservations, appointments, shopping and sourcing, deliveries, errands and the requests that fit no category. You ask. We handle it.",
      s: "Whatever brings you to Paris — residence, business, family or frequent visits — daily life here runs on local knowledge, physical presence and fluent French.",
      c: "Without those three, small tasks consume large hours: the appointment that requires calling, the delivery that requires someone home, the errand that requires knowing the right address. Apps solve fragments; nobody owns the whole.",
      q: "What would your week look like with a trusted personal assistant in Paris?",
      deliverablesTitle: "What your private assistant handles",
      deliverables: [
        "Reservations — restaurants, appointments, services, at the right places",
        "Appointments — booked, prepared, attended with you or for you",
        "Shopping & sourcing — from daily needs to hard-to-find requests",
        "Deliveries — received, checked, installed, returned",
        "Personal errands — pharmacies, tailors, ateliers, administrations",
        "Special requests — if it can be done in Paris, we find the way",
      ],
      forTitle: "Who retains us",
      forWho: [
        "International residents who want one reliable person to call",
        "Frequent visitors who want Paris handled before they land",
        "Busy professionals whose time is worth more than logistics",
      ],
      faqs: [
        { q: "Is the assistant English-speaking?", a: "Yes — natively bilingual French and English. You instruct in English; everything Paris-facing happens in French." },
        { q: "Can I use the service for a single request?", a: "Yes. One-off assistance is welcome; ongoing needs fit our Essential, Executive or Private Office membership." },
        { q: "How discreet is the service?", a: "NDA by default on request; one named person on your file; bookings in our name where you prefer." },
      ],
      waMsg: "Hello, I am looking for a private assistant in Paris.",
    },
    {
      seoTitle: "Assistant personnel à Paris — bilingue, discret | Book & Smile",
      seoDesc: "Votre assistant personnel à Paris : réservations, rendez-vous, administration française, courses, sourcing et demandes spéciales. Discret, fiable, sur place.",
      eyebrow: "Assistant personnel · Paris",
      h1a: "Assistant personnel",
      h1b: "à Paris.",
      answer: "Une paire de mains discrète à Paris. Book & Smile vous donne un assistant personnel bilingue sur place — réservations, rendez-vous, achats et sourcing, livraisons, courses et les demandes qui n'entrent dans aucune case. Vous demandez. Nous gérons.",
      s: "Quelle que soit la raison qui vous amène à Paris — résidence, affaires, famille ou séjours fréquents — le quotidien y repose sur la connaissance locale, la présence physique et un français courant.",
      c: "Sans ces trois éléments, les petites tâches dévorent de grandes heures. Les applications résolvent des fragments ; personne ne porte l'ensemble.",
      q: "À quoi ressemblerait votre semaine avec un assistant personnel de confiance à Paris ?",
      deliverablesTitle: "Ce que votre assistant privé gère",
      deliverables: [
        "Réservations — restaurants, rendez-vous, services, aux bonnes adresses",
        "Rendez-vous — pris, préparés, honorés avec vous ou pour vous",
        "Achats & sourcing — du quotidien aux demandes introuvables",
        "Livraisons — réceptionnées, vérifiées, installées, retournées",
        "Courses personnelles — pharmacies, tailleurs, ateliers, administrations",
        "Demandes spéciales — si c'est faisable à Paris, nous trouvons le moyen",
      ],
      forTitle: "Qui nous retient",
      forWho: [
        "Résidents internationaux qui veulent une personne fiable à appeler",
        "Visiteurs fréquents qui veulent un Paris déjà géré à l'atterrissage",
        "Professionnels dont le temps vaut plus que la logistique",
      ],
      faqs: [
        { q: "L'assistant parle-t-il anglais ?", a: "Oui — bilingue natif français-anglais. Vous donnez vos instructions en anglais ; tout ce qui est tourné vers Paris se fait en français." },
        { q: "Puis-je faire une demande unique ?", a: "Oui. L'assistance ponctuelle est bienvenue ; les besoins réguliers relèvent des adhésions Essential, Executive ou Private Office." },
        { q: "Quel niveau de discrétion ?", a: "NDA par défaut sur demande ; une personne nommée sur votre dossier ; réservations à notre nom si vous préférez." },
      ],
      waMsg: "Bonjour, je cherche un assistant privé à Paris.",
    }
  ),

  "personal-assistant-paris": page(
    {
      seoTitle: "Personal Assistant in Paris — Daily Life | Book & Smile",
      seoDesc: "A personal assistant in Paris for your daily life: household coordination, family logistics, appointments, admin follow-up. English & French. Book & Smile.",
      eyebrow: "Personal Assistant · Paris",
      h1a: "A personal assistant",
      h1b: "for life in Paris.",
      answer: "Book & Smile provides a bilingual personal assistant in Paris for the recurring fabric of daily life — household, family logistics, appointments, administrative follow-up — so that living here feels as good as visiting.",
      s: "Living in Paris is a privilege built on a hundred small recurring tasks: the plumber, the school note, the utility letter, the renewal that arrives in French.",
      c: "Each is small. Together they occupy evenings and weekends — or they occupy a professional who handles them in half the time, in the right language, at the right counter.",
      q: "Which of those tasks actually needs to be done by you?",
      deliverablesTitle: "Daily life, handled",
      deliverables: [
        "Household coordination — contractors, maintenance, deliveries, staff",
        "Family logistics — schools, activities, healthcare appointments",
        "Administrative follow-up — letters translated, answered, filed",
        "Recurring errands — on a rhythm you define once",
        "Home oversight while you travel — mail, plants, security checks",
        "The unexpected — a burst pipe, a lost key, a last-minute change",
      ],
      forTitle: "Who retains us",
      forWho: [
        "Families settled in Paris who want their evenings back",
        "Professionals living alone with no time for logistics",
        "Part-year residents who need continuity between stays",
      ],
      faqs: [
        { q: "How is this different from your private assistant service?", a: "Same standard, different rhythm: this page describes ongoing daily-life support, typically under Essential or Executive membership." },
        { q: "Can you hold keys to our home?", a: "Yes, under a signed key protocol with logged access and written reports." },
        { q: "Do you cover the suburbs?", a: "Yes — all of Paris and the Île-de-France, including Neuilly, Boulogne, Saint-Germain-en-Laye and Versailles." },
      ],
      waMsg: "Hello, I am looking for a personal assistant in Paris for daily life.",
    },
    {
      seoTitle: "Assistant personnel à Paris — le quotidien, géré | Book & Smile",
      seoDesc: "Un assistant personnel à Paris pour votre quotidien : coordination de la maison, logistique familiale, rendez-vous, suivi administratif. Book & Smile.",
      eyebrow: "Personal assistant · Paris",
      h1a: "Un assistant personnel",
      h1b: "pour la vie à Paris.",
      answer: "Book & Smile fournit un assistant personnel bilingue à Paris pour le tissu récurrent du quotidien — maison, logistique familiale, rendez-vous, suivi administratif — pour que vivre ici soit aussi agréable que visiter.",
      s: "Vivre à Paris est un privilège bâti sur cent petites tâches récurrentes : le plombier, le mot de l'école, le courrier du fournisseur, le renouvellement qui arrive en français.",
      c: "Chacune est petite. Ensemble, elles occupent les soirées et les week-ends — ou un professionnel qui les traite deux fois plus vite, dans la bonne langue, au bon guichet.",
      q: "Lesquelles de ces tâches doivent vraiment être faites par vous ?",
      deliverablesTitle: "Le quotidien, géré",
      deliverables: [
        "Coordination de la maison — artisans, entretien, livraisons, personnel",
        "Logistique familiale — écoles, activités, rendez-vous santé",
        "Suivi administratif — courriers traduits, traités, classés",
        "Courses récurrentes — sur un rythme défini une fois",
        "Supervision du domicile en votre absence — courrier, contrôles",
        "L'imprévu — une fuite, une clé perdue, un changement de dernière minute",
      ],
      forTitle: "Qui nous retient",
      forWho: [
        "Familles installées à Paris qui veulent retrouver leurs soirées",
        "Professionnels sans temps pour la logistique",
        "Résidents partiels qui ont besoin de continuité entre les séjours",
      ],
      faqs: [
        { q: "Quelle différence avec votre service d'assistant privé ?", a: "Même standard, autre rythme : cette page décrit le soutien quotidien continu, typiquement sous adhésion Essential ou Executive." },
        { q: "Pouvez-vous détenir nos clés ?", a: "Oui, sous protocole signé avec accès consignés et rapports écrits." },
        { q: "Couvrez-vous la banlieue ?", a: "Oui — tout Paris et l'Île-de-France, dont Neuilly, Boulogne, Saint-Germain-en-Laye et Versailles." },
      ],
      waMsg: "Bonjour, je cherche un assistant personnel à Paris pour le quotidien.",
    }
  ),

  "executive-assistant-paris": page(
    {
      seoTitle: "Executive Assistant in Paris — On the Ground | Book & Smile",
      seoDesc: "Your executive assistant on the ground in Paris: meetings, travel coordination, business logistics, guest management and local representation for leaders.",
      eyebrow: "Executive Assistance · Paris",
      h1a: "Your executive assistant",
      h1b: "on the ground in Paris.",
      answer: "Book & Smile provides executive assistant services in Paris for CEOs, founders, executives and investors: meeting arrangements, travel coordination, business logistics, guest management and local representation — bilingual, discreet, accountable in writing.",
      s: "You run your business from elsewhere. Paris is a market, a partner, a board seat, a client — something that requires presence a few days a month.",
      c: "Your team is excellent, and absent: nobody to confirm the meeting in French, receive the delegation, prepare the room, or simply be there when presence is the point.",
      q: "Who represents you in Paris when you cannot be there?",
      deliverablesTitle: "What we handle for executives",
      deliverables: [
        "Meeting arrangements — scheduled, confirmed, prepared, minuted",
        "Travel coordination — flights, cars, hotels, restaurants, contingencies",
        "Business logistics — documents, signatures, couriers, printing, venues",
        "Event organisation — dinners, boards, product moments",
        "Guest management — your clients and partners received properly",
        "Local representation — a professional presence at appointments in your name",
      ],
      forTitle: "Who retains us",
      forWho: [
        "CEOs and founders with recurring business in Paris",
        "Investors managing French interests from abroad",
        "International companies without a Paris office",
      ],
      faqs: [
        { q: "Do you work with our existing EA?", a: "Seamlessly. Your assistant keeps ownership; we execute the Paris layer and report back in writing." },
        { q: "Can you attend meetings on our behalf?", a: "Yes — with a written brief and mandate, we attend, represent and report." },
        { q: "What does it cost?", a: "Executive membership covers regular needs; one-off missions are quoted per request. Terms defined in a private consultation." },
      ],
      waMsg: "Hello, I am interested in executive assistant services in Paris.",
    },
    {
      seoTitle: "Executive assistant à Paris — sur le terrain | Book & Smile",
      seoDesc: "Votre executive assistant sur le terrain à Paris : réunions, déplacements, logistique business, gestion d'invités et représentation locale pour dirigeants.",
      eyebrow: "Assistance exécutive · Paris",
      h1a: "Votre executive assistant",
      h1b: "sur le terrain à Paris.",
      answer: "Book & Smile fournit des services d'executive assistant à Paris pour dirigeants, fondateurs et investisseurs : organisation de réunions, coordination de déplacements, logistique business, gestion d'invités et représentation locale — bilingue, discret, responsable par écrit.",
      s: "Vous dirigez votre activité d'ailleurs. Paris est un marché, un partenaire, un siège au conseil, un client — quelque chose qui exige une présence quelques jours par mois.",
      c: "Votre équipe est excellente, et absente : personne pour confirmer la réunion en français, accueillir la délégation, préparer la salle, ou simplement être là quand la présence est l'essentiel.",
      q: "Qui vous représente à Paris quand vous ne pouvez pas y être ?",
      deliverablesTitle: "Ce que nous gérons pour les dirigeants",
      deliverables: [
        "Réunions — planifiées, confirmées, préparées, avec compte rendu",
        "Déplacements — vols, voitures, hôtels, restaurants, imprévus",
        "Logistique business — documents, signatures, coursiers, salles",
        "Événements — dîners, conseils, moments produits",
        "Gestion d'invités — vos clients et partenaires reçus comme il se doit",
        "Représentation locale — une présence professionnelle en votre nom",
      ],
      forTitle: "Qui nous retient",
      forWho: [
        "Dirigeants et fondateurs avec des affaires récurrentes à Paris",
        "Investisseurs gérant des intérêts français depuis l'étranger",
        "Sociétés internationales sans bureau parisien",
      ],
      faqs: [
        { q: "Travaillez-vous avec notre assistante actuelle ?", a: "En toute fluidité. Votre assistante garde la main ; nous exécutons la couche parisienne et rendons compte par écrit." },
        { q: "Pouvez-vous assister à des réunions pour nous ?", a: "Oui — avec un brief écrit et un mandat, nous assistons, représentons et rendons compte." },
        { q: "Quel est le coût ?", a: "L'adhésion Executive couvre les besoins réguliers ; les missions ponctuelles sont devisées. Conditions définies en consultation privée." },
      ],
      waMsg: "Bonjour, je suis intéressé(e) par des services d'executive assistant à Paris.",
    }
  ),

  "private-concierge-paris": page(
    {
      seoTitle: "Private Concierge in Paris — Not a Hotel Desk | Book & Smile",
      seoDesc: "Private concierge services in Paris: restaurants, experiences, shopping, events, travel and last-minute requests — from someone who knows you, not a hotel desk.",
      eyebrow: "Private Concierge · Paris",
      h1a: "Private concierge services",
      h1b: "in Paris.",
      answer: "Book & Smile is a private concierge in Paris that actually knows you: restaurants, experiences, shopping, events, travel and last-minute requests, handled by one named person who keeps your preferences on file — not a desk that resets with every guest.",
      s: "Paris rewards those who know whom to call. The right table, the private visit, the impossible ticket — all exist, behind relationships.",
      c: "Hotel concierges serve whoever is in the lobby tonight. Apps serve their inventory. Neither remembers your allergies, your favourite floor, or that your daughter's birthday is in May.",
      q: "What changes when your concierge knows you?",
      deliverablesTitle: "What we arrange",
      deliverables: [
        "Restaurants — the right table, under your preferences, at short notice",
        "Experiences — private visits, ateliers, moments not offered publicly",
        "Shopping — sourcing, personal shopping, gifting with your card",
        "Events — premieres, openings, sold-out evenings",
        "Travel — transfers, day trips, seamless arrivals and departures",
        "Last-minute requests — the speciality of the house",
      ],
      forTitle: "Who retains us",
      forWho: [
        "Residents who want one concierge who remembers",
        "Regular visitors tired of starting from zero each trip",
        "Hosts who want their guests impressed, quietly",
      ],
      faqs: [
        { q: "How fast can you respond?", a: "Members reach us directly; same-day requests are the norm, not the exception." },
        { q: "Do you charge per request?", a: "Concierge access is part of membership; one-off requests are quoted simply. Third-party costs at cost, pre-approved." },
        { q: "How is this different from a hotel concierge?", a: "Continuity. One named person, your preferences on file, and no reset at checkout." },
      ],
      waMsg: "Hello, I am interested in private concierge services in Paris.",
    },
    {
      seoTitle: "Conciergerie privée à Paris, sur mesure | Book & Smile",
      seoDesc: "Services de conciergerie privée à Paris : restaurants, expériences, shopping, événements, voyages et demandes de dernière minute — par quelqu'un qui vous connaît.",
      eyebrow: "Conciergerie privée · Paris",
      h1a: "Conciergerie privée",
      h1b: "à Paris.",
      answer: "Book & Smile est une conciergerie privée à Paris qui vous connaît vraiment : restaurants, expériences, shopping, événements, voyages et demandes de dernière minute, gérés par une personne nommée qui garde vos préférences en mémoire — pas un desk qui se réinitialise à chaque client.",
      s: "Paris récompense ceux qui savent qui appeler. La bonne table, la visite privée, le billet impossible — tout existe, derrière des relations.",
      c: "Les concierges d'hôtel servent qui est dans le hall ce soir. Les applications servent leur inventaire. Aucun ne se souvient de vos allergies ni de l'anniversaire de votre fille en mai.",
      q: "Qu'est-ce qui change quand votre concierge vous connaît ?",
      deliverablesTitle: "Ce que nous organisons",
      deliverables: [
        "Restaurants — la bonne table, selon vos préférences, au pied levé",
        "Expériences — visites privées, ateliers, moments hors catalogue",
        "Shopping — sourcing, personal shopping, cadeaux avec votre carte",
        "Événements — premières, vernissages, soirées complètes",
        "Voyages — transferts, escapades, arrivées et départs fluides",
        "Dernière minute — la spécialité de la maison",
      ],
      forTitle: "Qui nous retient",
      forWho: [
        "Résidents qui veulent un concierge qui se souvient",
        "Visiteurs réguliers lassés de repartir de zéro à chaque séjour",
        "Hôtes qui veulent impressionner leurs invités, discrètement",
      ],
      faqs: [
        { q: "Quelle réactivité ?", a: "Les membres nous joignent directement ; les demandes du jour même sont la norme." },
        { q: "Facturez-vous à la demande ?", a: "L'accès conciergerie fait partie de l'adhésion ; les demandes ponctuelles sont devisées simplement. Coûts tiers au réel, pré-approuvés." },
        { q: "Quelle différence avec un concierge d'hôtel ?", a: "La continuité. Une personne nommée, vos préférences en mémoire, aucun reset au check-out." },
      ],
      waMsg: "Bonjour, je suis intéressé(e) par une conciergerie privée à Paris.",
    }
  ),

  "luxury-concierge-paris": page(
    {
      seoTitle: "Luxury Concierge in Paris — Rare Access | Book & Smile",
      seoDesc: "A luxury concierge in Paris for rare access: private ateliers, haute cuisine, after-hours culture, exceptional gifting and bespoke experiences, discreetly.",
      eyebrow: "Luxury Concierge · Paris",
      h1a: "Luxury, arranged",
      h1b: "without noise.",
      answer: "Book & Smile is a luxury concierge in Paris for clients who want the exceptional handled quietly: private ateliers, haute cuisine, after-hours cultural access, exceptional gifting and bespoke experiences — never flashy, always confirmed in writing.",
      s: "True luxury in Paris is rarely on sale. It is a maison that opens its atelier, a chef who cooks off-menu, a museum after the doors close.",
      c: "That tier answers introductions, not requests. Arriving without one, even unlimited budgets wait in the same queue as everyone else.",
      q: "Who introduces you to the Paris that is not for sale?",
      deliverablesTitle: "The exceptional, handled",
      deliverables: [
        "Private ateliers & maisons — visits and commissions by introduction",
        "Haute cuisine — chef's tables, off-menu, private chefs at home",
        "Culture after hours — private viewings, curator-led moments",
        "Exceptional gifting — sourced, commissioned, delivered flawlessly",
        "Bespoke experiences — designed once, for you only",
        "Absolute discretion — NDA by default, no names, ever",
      ],
      forTitle: "Who retains us",
      forWho: [
        "Principals for whom standard luxury has become ordinary",
        "Family offices arranging exceptional moments for principals",
        "Collectors and connoisseurs seeking genuine access",
      ],
      faqs: [
        { q: "Is there anything you cannot arrange?", a: "Yes — and we say so immediately. What we confirm, happens. That honesty is why the introductions stay open." },
        { q: "Do you publish prices?", a: "No. Bespoke work is quoted per project after a private consultation." },
        { q: "Can experiences remain completely anonymous?", a: "Yes. Arrangements can be made entirely in the house's name." },
      ],
      waMsg: "Hello, I am interested in luxury concierge services in Paris.",
    },
    {
      seoTitle: "Conciergerie de luxe à Paris — l'accès rare | Book & Smile",
      seoDesc: "Une conciergerie de luxe à Paris pour l'accès rare : ateliers privés, haute gastronomie, culture après fermeture, cadeaux d'exception, expériences sur mesure.",
      eyebrow: "Conciergerie de luxe · Paris",
      h1a: "Le luxe, organisé",
      h1b: "sans bruit.",
      answer: "Book & Smile est une conciergerie de luxe à Paris pour les clients qui veulent l'exceptionnel géré en silence : ateliers privés, haute gastronomie, accès culturel après fermeture, cadeaux d'exception et expériences sur mesure — jamais ostentatoire, toujours confirmé par écrit.",
      s: "Le vrai luxe à Paris est rarement en vente. C'est une maison qui ouvre son atelier, un chef qui cuisine hors carte, un musée après la fermeture.",
      c: "Ce niveau répond aux introductions, pas aux demandes. Sans introduction, même les budgets illimités attendent dans la même file que tout le monde.",
      q: "Qui vous introduit au Paris qui n'est pas à vendre ?",
      deliverablesTitle: "L'exceptionnel, géré",
      deliverables: [
        "Ateliers & maisons privés — visites et commandes sur introduction",
        "Haute gastronomie — tables de chef, hors carte, chefs à domicile",
        "Culture après fermeture — visites privées, moments avec conservateurs",
        "Cadeaux d'exception — sourcés, commandés, livrés sans faute",
        "Expériences sur mesure — conçues une fois, pour vous seul",
        "Discrétion absolue — NDA par défaut, aucun nom, jamais",
      ],
      forTitle: "Qui nous retient",
      forWho: [
        "Principals pour qui le luxe standard est devenu ordinaire",
        "Family offices organisant des moments d'exception",
        "Collectionneurs et connaisseurs en quête d'accès véritable",
      ],
      faqs: [
        { q: "Y a-t-il des choses impossibles ?", a: "Oui — et nous le disons immédiatement. Ce que nous confirmons a lieu. Cette honnêteté est ce qui garde les introductions ouvertes." },
        { q: "Publiez-vous des tarifs ?", a: "Non. Le sur-mesure est devisé par projet, après consultation privée." },
        { q: "Les expériences peuvent-elles rester anonymes ?", a: "Oui. Tout peut être organisé au nom de la maison." },
      ],
      waMsg: "Bonjour, je suis intéressé(e) par une conciergerie de luxe à Paris.",
    }
  ),
};
